import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  phone: "",
  email: "",
  property_name: "",
  number_of_units: "",
  message: "",
};

export const ContactForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Proposal request sent! We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-sm text-[#1E293B] placeholder:text-slate-400 outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition";
  const labelClass = "block text-sm font-semibold text-[#1E293B] mb-1.5";

  if (done) {
    return (
      <div
        data-testid="contact-success"
        className="flex flex-col items-center justify-center text-center rounded-2xl bg-white border border-gray-100 shadow-xl p-10 min-h-[420px]"
      >
        <CheckCircle2 className="h-16 w-16 text-[#2563EB]" />
        <h3 className="mt-5 font-display text-2xl font-bold text-[#1E293B]">
          Request Received
        </h3>
        <p className="mt-3 text-[#475569] max-w-sm">
          Thank you! Our team will review your community's needs and reach out
          with a free, no-obligation proposal.
        </p>
        <button
          data-testid="contact-reset-button"
          onClick={() => setDone(false)}
          className="mt-6 rounded-md border-2 border-[#1E293B] px-5 py-2.5 text-sm font-semibold text-[#1E293B] hover:bg-[#F1F5F9] transition-colors"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className="rounded-2xl bg-white border border-gray-100 shadow-xl p-6 sm:p-8 space-y-5"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Name *</label>
          <input
            name="name"
            value={form.name}
            onChange={update}
            data-testid="contact-input-name"
            placeholder="John Smith"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Phone Number *</label>
          <input
            name="phone"
            value={form.phone}
            onChange={update}
            data-testid="contact-input-phone"
            placeholder="(912) 555-0123"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Email *</label>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={update}
            data-testid="contact-input-email"
            placeholder="you@company.com"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Property Name</label>
          <input
            name="property_name"
            value={form.property_name}
            onChange={update}
            data-testid="contact-input-property"
            placeholder="Riverside Apartments"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>Number of Units</label>
        <input
          name="number_of_units"
          value={form.number_of_units}
          onChange={update}
          data-testid="contact-input-units"
          placeholder="e.g. 250"
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Message</label>
        <textarea
          name="message"
          value={form.message}
          onChange={update}
          data-testid="contact-input-message"
          rows={4}
          placeholder="Tell us about your community and what you're looking for..."
          className={inputClass + " resize-none"}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit-button"
        className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#2563EB] px-6 py-3.5 text-base font-semibold text-white hover:bg-[#1D4ED8] transition-colors shadow-sm disabled:opacity-70"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Sending...
          </>
        ) : (
          <>
            Request Proposal <Send className="h-4 w-4" />
          </>
        )}
      </button>
      <p className="text-xs text-center text-slate-400">
        Free, no-obligation proposal · We respond within one business day.
      </p>
    </form>
  );
};
